import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Link,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAppSettings } from '../context/SettingsContext';
import {
  testXeroConnection,
  testDudaConnection,
  testGoogleAdsConnection,
} from '../services/integrationTests';
import { testStripeConnectionResolved } from '../services/stripeResolved';
import {
  testSmtpConnection,
  testSearchConsoleConnection,
  testGa4Connection,
  testMerchantCenterConnection,
  testSlackConnection,
  testTwilioConnection,
} from '../services/integrationTestsExtra';
import { useNavigate } from 'react-router-dom';
import xeroAuth, { previewXeroRedirectUri } from '../services/xeroAuth';
import { apiFetch, apiUrl, useBackendAuth } from '../services/apiClient';
import { SecretField } from '../components/settings/SecretField';
import { isIntegrationSecretConfigured } from '../services/secretConfigured';
import { createDudaAccount } from '../settings/dudaAccounts';
import { createMetaAccount } from '../settings/metaAccounts';
import { testMetaAccountConnection } from '../services/metaApi';
import { testWizzardConnection } from '../services/wizzardApi';
import {
  dudaPasswordSecretPath,
  metaAccessTokenSecretPath,
  SECRET_FIELD_PATHS,
  type AppSettings,
  type DudaAccount,
  type MetaAccount,
} from '../settings/types';

type SectionId =
  | 'smtp'
  | 'stripe'
  | 'xero'
  | 'duda'
  | 'meta'
  | 'search-console'
  | 'google-ads'
  | 'ga4'
  | 'merchant-center'
  | 'slack'
  | 'twilio'
  | 'wizzard';

const SECTIONS: { id: SectionId; label: string; icon: React.ReactNode; hash: string[] }[] = [
  { id: 'smtp', label: 'Google Email (SMTP)', icon: <EmailOutlinedIcon />, hash: ['smtp', 'email'] },
  { id: 'stripe', label: 'Stripe', icon: <CreditCardOutlinedIcon />, hash: ['stripe'] },
  { id: 'xero', label: 'Xero', icon: <AccountBalanceOutlinedIcon />, hash: ['xero'] },
  { id: 'duda', label: 'Duda', icon: <LanguageOutlinedIcon />, hash: ['duda'] },
  { id: 'meta', label: 'Facebook & Instagram', icon: <FacebookOutlinedIcon />, hash: ['meta', 'facebook', 'instagram'] },
  { id: 'search-console', label: 'Search Console', icon: <SearchOutlinedIcon />, hash: ['search-console', 'searchconsole'] },
  { id: 'google-ads', label: 'Google Ads', icon: <CampaignOutlinedIcon />, hash: ['google-ads', 'ads', 'googleads'] },
  { id: 'ga4', label: 'GA4', icon: <AnalyticsOutlinedIcon />, hash: ['ga4', 'analytics'] },
  { id: 'merchant-center', label: 'Merchant Center', icon: <StorefrontOutlinedIcon />, hash: ['merchant-center', 'merchantcenter'] },
  { id: 'slack', label: 'Slack', icon: <ChatOutlinedIcon />, hash: ['slack'] },
  { id: 'twilio', label: 'Twilio', icon: <PhoneOutlinedIcon />, hash: ['twilio'] },
  { id: 'wizzard', label: 'Wizzard (Grok)', icon: <AutoFixHighIcon />, hash: ['wizzard', 'grok'] },
];

function hashToSection(hash: string): SectionId {
  const h = hash.replace('#', '').toLowerCase();
  for (const s of SECTIONS) {
    if (s.hash.includes(h)) {
      return s.id;
    }
  }
  return 'smtp';
}

const ApiSettings: React.FC = () => {
  const {
    settings,
    updateSettings,
    replaceIntegrations,
    configuredSecrets,
    secretsStorage,
    isSecretConfigured,
  } = useAppSettings();
  const [section, setSection] = useState<SectionId>('smtp');
  const [testing, setTesting] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [draft, setDraft] = useState<AppSettings['integrations']>(settings.integrations);
  const [workspaceDraft, setWorkspaceDraft] = useState(settings.workspace.name);
  const [xeroRedirectUri, setXeroRedirectUri] = useState<string | null>(null);
  const backend = useBackendAuth;
  const navigate = useNavigate();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const xeroError = searchParams.get('xero_error');

  useEffect(() => {
    setDraft(settings.integrations);
    setWorkspaceDraft(settings.workspace.name);
  }, [settings]);

  useEffect(() => {
    if (!backend) {
      return;
    }
    void apiFetch<{ redirectUri: string }>('/api/xero/status')
      .then((s) => setXeroRedirectUri(s.redirectUri))
      .catch(() => setXeroRedirectUri(null));
  }, [backend, settings.integrations.xero]);

  useEffect(() => {
    const applyHash = () => setSection(hashToSection(window.location.hash));
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  useEffect(() => {
    if (xeroError && section !== 'xero') {
      setSection('xero');
      window.location.hash = 'xero';
    }
  }, [xeroError, section]);

  const selectSection = (id: SectionId) => {
    setSection(id);
    const s = SECTIONS.find((x) => x.id === id);
    if (s) {
      window.location.hash = s.hash[0];
    }
  };

  const secretProps = useMemo(
    () => ({ configuredSecrets, backendMode: backend }),
    [configuredSecrets, backend]
  );

  const runTest = async (key: string) => {
    setMessage(null);
    setTesting(key);
    try {
      let r: { ok: boolean; label?: string; message?: string };
      switch (key) {
        case 'smtp':
          r = await testSmtpConnection(draft.smtp, isSecretConfigured);
          break;
        case 'stripe':
          r = await testStripeConnectionResolved(settings, isSecretConfigured, draft.stripe.secretKey);
          break;
        case 'xero': {
          const clientId = draft.xero.clientId.trim();
          const secretOk = isIntegrationSecretConfigured(
            isSecretConfigured,
            SECRET_FIELD_PATHS.xeroClientSecret,
            draft.xero.clientSecret
          );
          if (!clientId || !secretOk) {
            r = { ok: false, message: 'Client ID and Client Secret are required — click Save after pasting' };
            break;
          }
          if (backend) {
            try {
              const status = await apiFetch<{ connected: boolean }>('/api/xero/status');
              r = status.connected
                ? { ok: true, label: 'Connected to Xero' }
                : { ok: false, message: 'Credentials saved — use Connect Xero to finish OAuth' };
            } catch (e) {
              r = { ok: false, message: e instanceof Error ? e.message : 'Test failed' };
            }
          } else {
            r = await testXeroConnection({
              ...settings,
              integrations: { ...settings.integrations, xero: draft.xero },
            });
          }
          break;
        }
        case 'googleAds':
          r = await testGoogleAdsConnection(draft.googleAds);
          break;
        case 'searchConsole':
          r = await testSearchConsoleConnection(draft.searchConsole, isSecretConfigured);
          break;
        case 'ga4':
          r = await testGa4Connection(draft.ga4, isSecretConfigured);
          break;
        case 'merchantCenter':
          r = await testMerchantCenterConnection(draft.merchantCenter, isSecretConfigured);
          break;
        case 'slack':
          r = await testSlackConnection(draft.slack, isSecretConfigured);
          break;
        case 'twilio':
          r = await testTwilioConnection(draft.twilio, isSecretConfigured);
          break;
        case 'wizzard':
          r = await testWizzardConnection(
            { ...settings, integrations: { ...settings.integrations, wizzard: draft.wizzard } },
            isSecretConfigured
          );
          break;
        default:
          if (key.startsWith('duda-')) {
            const id = key.replace('duda-', '');
            const acc = draft.duda.accounts.find((a) => a.id === id);
            if (!acc) {
              return;
            }
            const pwdOk = isIntegrationSecretConfigured(
              isSecretConfigured,
              dudaPasswordSecretPath(acc.id),
              acc.apiPassword
            );
            if (!acc.apiUser.trim() || !pwdOk) {
              r = { ok: false, message: 'API user and password are required — save first if password is stored securely' };
            } else if (!acc.apiPassword.trim() && backend) {
              r = { ok: true, label: 'Credentials saved securely — live Duda test needs password in field or server proxy' };
            } else {
              r = await testDudaConnection(acc.apiUser, acc.apiPassword, acc.siteName);
            }
          } else if (key.startsWith('meta-')) {
            const id = key.replace('meta-', '');
            const acc = draft.meta.accounts.find((a) => a.id === id);
            if (!acc) {
              return;
            }
            const tokenOk = isIntegrationSecretConfigured(
              isSecretConfigured,
              metaAccessTokenSecretPath(acc.id),
              acc.accessToken
            );
            if (!tokenOk) {
              r = { ok: false, message: 'Access token is required — save first if token is stored securely' };
            } else if (!acc.accessToken.trim() && backend) {
              r = { ok: true, label: 'Token saved securely — live Meta test needs token in field or server API' };
            } else {
              r = await testMetaAccountConnection(acc, draft.meta.appId);
            }
          } else {
            return;
          }
      }
      setMessage(r.ok ? { type: 'success', text: r.label || 'OK' } : { type: 'error', text: r.message || 'Failed' });
    } finally {
      setTesting(null);
    }
  };

  const saveSection = async (key: keyof AppSettings['integrations']) => {
    setMessage(null);
    try {
      await replaceIntegrations(key, draft[key]);
      const stored =
        backend && secretsStorage === 'secret-manager'
          ? 'Saved securely to Google Secret Manager'
          : backend
            ? 'Saved to server'
            : 'Saved';
      setMessage({ type: 'success', text: stored });
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Save failed' });
    }
  };

  const patchDudaAccount = (index: number, patch: Partial<DudaAccount>) => {
    setDraft((d) => ({
      ...d,
      duda: {
        ...d.duda,
        accounts: d.duda.accounts.map((a, i) => (i === index ? { ...a, ...patch } : a)),
      },
    }));
  };

  const addDudaAccount = () => {
    const acc = createDudaAccount(`Account ${draft.duda.accounts.length + 1}`);
    setDraft((d) => ({
      ...d,
      duda: { ...d.duda, accounts: [...d.duda.accounts, acc] },
    }));
  };

  const removeDudaAccount = (index: number) => {
    if (draft.duda.accounts.length <= 1) {
      return;
    }
    setDraft((d) => {
      const accounts = d.duda.accounts.filter((_, i) => i !== index);
      const activeAccountId = accounts.some((a) => a.id === d.duda.activeAccountId)
        ? d.duda.activeAccountId
        : accounts[0].id;
      return { ...d, duda: { accounts, activeAccountId } };
    });
  };

  const patchMetaAccount = (index: number, patch: Partial<MetaAccount>) => {
    setDraft((d) => ({
      ...d,
      meta: {
        ...d.meta,
        accounts: d.meta.accounts.map((a, i) => (i === index ? { ...a, ...patch } : a)),
      },
    }));
  };

  const addMetaAccount = () => {
    const acc = createMetaAccount(`Account ${draft.meta.accounts.length + 1}`);
    setDraft((d) => ({
      ...d,
      meta: { ...d.meta, accounts: [...d.meta.accounts, acc] },
    }));
  };

  const removeMetaAccount = (index: number) => {
    if (draft.meta.accounts.length <= 1) {
      return;
    }
    setDraft((d) => {
      const accounts = d.meta.accounts.filter((_, i) => i !== index);
      const activeAccountId = accounts.some((a) => a.id === d.meta.activeAccountId)
        ? d.meta.activeAccountId
        : accounts[0].id;
      return { ...d, meta: { ...d.meta, accounts, activeAccountId } };
    });
  };

  const SaveTestRow = ({ testKey, saveKey }: { testKey: string; saveKey: keyof AppSettings['integrations'] }) => (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      <Button
        variant="outlined"
        startIcon={testing === testKey ? <CircularProgress size={18} /> : <ScienceOutlinedIcon />}
        disabled={testing !== null}
        onClick={() => void runTest(testKey)}
      >
        Test
      </Button>
      <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={() => void saveSection(saveKey)}>
        Save
      </Button>
    </Stack>
  );

  const renderPanel = () => {
    switch (section) {
      case 'smtp':
        return (
          <Stack spacing={2} maxWidth={640}>
            <Alert severity="info">
              Gmail requires an <strong>App password</strong> (16 characters) — your normal Google password will not
              work. Enable{' '}
              <Link href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noopener noreferrer">
                2-Step Verification
              </Link>
              , then create an app password under{' '}
              <Link href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
                App passwords
              </Link>
              . Use host <code>smtp.gmail.com</code>, port <code>587</code>, TLS on. Click <strong>Save</strong> then{' '}
              <strong>Test</strong>.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Used for Xero payment reminders and Stripe billing emails. Password is stored in{' '}
              {backend ? 'Google Secret Manager' : 'local storage'}.
            </Typography>
            <TextField
              label="SMTP host"
              fullWidth
              value={draft.smtp.host}
              onChange={(e) => setDraft((d) => ({ ...d, smtp: { ...d.smtp, host: e.target.value } }))}
            />
            <TextField
              label="Port"
              type="number"
              fullWidth
              value={draft.smtp.port}
              onChange={(e) => setDraft((d) => ({ ...d, smtp: { ...d.smtp, port: Number(e.target.value) || 587 } }))}
            />
            <TextField
              label="Username (Google email)"
              fullWidth
              value={draft.smtp.username}
              onChange={(e) => setDraft((d) => ({ ...d, smtp: { ...d.smtp, username: e.target.value } }))}
            />
            <SecretField
              label="App password"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.smtpPassword}
              {...secretProps}
              value={draft.smtp.password}
              onChange={(e) => setDraft((d) => ({ ...d, smtp: { ...d.smtp, password: e.target.value } }))}
              helperText="Google Account → Security → App passwords"
            />
            <TextField
              label="From email"
              fullWidth
              value={draft.smtp.fromEmail}
              onChange={(e) => setDraft((d) => ({ ...d, smtp: { ...d.smtp, fromEmail: e.target.value } }))}
            />
            <TextField
              label="Reply-To / AR alias (optional)"
              fullWidth
              placeholder="e.g. accounts@yourdomain.com — used for tracking customer replies"
              value={draft.smtp.replyToEmail}
              onChange={(e) => setDraft((d) => ({ ...d, smtp: { ...d.smtp, replyToEmail: e.target.value } }))}
              helperText="If set, reminders use this address (with a unique +tag per email). Replies land in this inbox — log them on the invoice trail."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.smtp.useTls}
                  onChange={(e) => setDraft((d) => ({ ...d, smtp: { ...d.smtp, useTls: e.target.checked } }))}
                />
              }
              label="Use TLS (STARTTLS on port 587)"
            />
            <SaveTestRow testKey="smtp" saveKey="smtp" />
          </Stack>
        );

      case 'stripe':
        return (
          <Stack spacing={2} maxWidth={640}>
            <TextField
              label="Publishable key"
              fullWidth
              value={draft.stripe.publishableKey}
              onChange={(e) => setDraft((d) => ({ ...d, stripe: { ...d.stripe, publishableKey: e.target.value } }))}
              helperText="pk_test_… or pk_live_…"
            />
            <SecretField
              label="Secret key"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.stripeSecretKey}
              {...secretProps}
              value={draft.stripe.secretKey}
              onChange={(e) => setDraft((d) => ({ ...d, stripe: { ...d.stripe, secretKey: e.target.value } }))}
            />
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Recurring invoices & reminders
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.stripe.billing.autoSendInvoices}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      stripe: { ...d.stripe, billing: { ...d.stripe.billing, autoSendInvoices: e.target.checked } },
                    }))
                  }
                />
              }
              label="Auto-send invoices after creation"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.stripe.billing.reminderEnabled}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      stripe: { ...d.stripe, billing: { ...d.stripe.billing, reminderEnabled: e.target.checked } },
                    }))
                  }
                />
              }
              label="Enable payment reminders"
            />
            <TextField
              label="Days until due"
              type="number"
              fullWidth
              value={draft.stripe.billing.daysUntilDue}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  stripe: {
                    ...d.stripe,
                    billing: { ...d.stripe.billing, daysUntilDue: Number(e.target.value) || 14 },
                  },
                }))
              }
            />
            <TextField
              label="Remind days before due (comma-separated)"
              fullWidth
              value={draft.stripe.billing.reminderDaysBefore.join(', ')}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  stripe: {
                    ...d.stripe,
                    billing: {
                      ...d.stripe.billing,
                      reminderDaysBefore: e.target.value
                        .split(',')
                        .map((x) => parseInt(x.trim(), 10))
                        .filter((n) => !Number.isNaN(n)),
                    },
                  },
                }))
              }
              helperText="e.g. 7, 3, 1"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Reminder channel</InputLabel>
              <Select
                label="Reminder channel"
                value={draft.stripe.billing.reminderChannel}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    stripe: {
                      ...d.stripe,
                      billing: {
                        ...d.stripe.billing,
                        reminderChannel: e.target.value as 'email' | 'stripe' | 'both',
                      },
                    },
                  }))
                }
              >
                <MenuItem value="both">Stripe + Email (SMTP)</MenuItem>
                <MenuItem value="stripe">Stripe only</MenuItem>
                <MenuItem value="email">Email only</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Default currency"
              fullWidth
              value={draft.stripe.billing.defaultCurrency}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  stripe: { ...d.stripe, billing: { ...d.stripe.billing, defaultCurrency: e.target.value } },
                }))
              }
            />
            <Alert severity="info">
              Manage invoices on the{' '}
              <Link component="button" variant="body2" onClick={() => navigate('/billing')}>
                Billing & reminders
              </Link>{' '}
              page after saving.
            </Alert>
            <SaveTestRow testKey="stripe" saveKey="stripe" />
          </Stack>
        );

      case 'xero':
        return (
          <Stack spacing={2} maxWidth={720}>
            {xeroError && section === 'xero' && (
              <Alert severity="error" onClose={() => window.history.replaceState({}, '', '/settings#xero')}>
                <strong>OAuth Error:</strong> {decodeURIComponent(xeroError)}
                {xeroError.includes('redirect_uri') && (
                  <Box sx={{ mt: 1 }}>
                    Make sure the redirect URI in your Xero Developer Portal exactly matches the one shown below.
                  </Box>
                )}
                {xeroError.includes('invalid_scope') && (
                  <Box sx={{ mt: 1 }}>
                    The scopes requested by this app may not be enabled in your Xero app configuration. Check the app
                    permissions in the Xero Developer Portal.
                  </Box>
                )}
              </Alert>
            )}
            <TextField
              label="Client ID"
              fullWidth
              value={draft.xero.clientId}
              onChange={(e) => setDraft((d) => ({ ...d, xero: { ...d.xero, clientId: e.target.value } }))}
            />
            <SecretField
              label="Client secret"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.xeroClientSecret}
              {...secretProps}
              value={draft.xero.clientSecret}
              onChange={(e) => setDraft((d) => ({ ...d, xero: { ...d.xero, clientSecret: e.target.value } }))}
            />
            <TextField
              label="OAuth redirect URI (optional override)"
              fullWidth
              placeholder="Leave blank for server default"
              value={draft.xero.redirectUri}
              onChange={(e) => setDraft((d) => ({ ...d, xero: { ...d.xero, redirectUri: e.target.value } }))}
            />
            <Alert severity="info">
              <strong>IMPORTANT:</strong> Register this exact redirect URI in the{' '}
              <Link href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer">
                Xero Developer Portal
              </Link>
              :
              <Typography component="code" display="block" sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, wordBreak: 'break-all', fontWeight: 700 }}>
                {backend && xeroRedirectUri ? xeroRedirectUri : previewXeroRedirectUri(draft.xero.redirectUri)}
              </Typography>
              Also ensure your Xero app has these scopes enabled: <strong>Accounting (Transactions, Contacts, Settings)</strong>
            </Alert>
            <Typography variant="subtitle2" fontWeight={700} sx={{ pt: 1 }}>
              Payment reminders
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.xero.collections?.attachPdf !== false}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      xero: {
                        ...d.xero,
                        collections: { ...d.xero.collections, attachPdf: e.target.checked },
                      },
                    }))
                  }
                />
              }
              label="Attach Xero invoice PDF by default"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.xero.collections?.stripePaymentLink !== false}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      xero: {
                        ...d.xero,
                        collections: { ...d.xero.collections, stripePaymentLink: e.target.checked },
                      },
                    }))
                  }
                />
              }
              label="Include Stripe card payment link when Stripe is configured"
            />
            <TextField
              label="AR reply-to alias (optional override)"
              fullWidth
              placeholder="Overrides SMTP reply-to for Xero reminders only"
              value={draft.xero.collections?.replyToEmail || ''}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  xero: {
                    ...d.xero,
                    collections: { ...d.xero.collections, replyToEmail: e.target.value },
                  },
                }))
              }
            />
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={testing === 'xero' ? <CircularProgress size={18} /> : <ScienceOutlinedIcon />}
                disabled={testing !== null}
                onClick={() => void runTest('xero')}
              >
                Test
              </Button>
              <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={() => void saveSection('xero')}>
                Save
              </Button>
              <Button
                variant="contained"
                color="secondary"
                endIcon={<OpenInNewIcon />}
                onClick={() => {
                  void (async () => {
                    try {
                      await replaceIntegrations('xero', draft.xero);
                      if (backend) {
                        window.location.assign(apiUrl('/api/xero/authorize'));
                      } else {
                        window.location.assign(xeroAuth.getAuthUrl());
                      }
                    } catch (e) {
                      alert(e instanceof Error ? e.message : 'Could not start OAuth');
                    }
                  })();
                }}
              >
                Connect Xero
              </Button>
            </Stack>
          </Stack>
        );

      case 'duda':
        return (
          <Stack spacing={3} maxWidth={720}>
            <Typography variant="body2" color="text.secondary">
              Add as many Duda partner accounts as you need. Passwords are stored per account in Secret Manager.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl fullWidth size="small" sx={{ maxWidth: 400 }}>
                <InputLabel id="duda-active-label">Default active account</InputLabel>
                <Select
                  labelId="duda-active-label"
                  label="Default active account"
                  value={draft.duda.activeAccountId}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, duda: { ...d.duda, activeAccountId: String(e.target.value) } }))
                  }
                >
                  {draft.duda.accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.label || a.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button startIcon={<AddIcon />} onClick={addDudaAccount}>
                Add account
              </Button>
            </Stack>
            {draft.duda.accounts.map((acc, index) => (
              <Card key={acc.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {acc.label || `Account ${index + 1}`}
                    </Typography>
                    {draft.duda.accounts.length > 1 && (
                      <IconButton size="small" color="error" onClick={() => removeDudaAccount(index)} aria-label="Remove">
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack spacing={2}>
                    <TextField
                      label="Menu label"
                      fullWidth
                      size="small"
                      value={acc.label}
                      onChange={(e) => patchDudaAccount(index, { label: e.target.value })}
                    />
                    <TextField
                      label="API user"
                      fullWidth
                      size="small"
                      value={acc.apiUser}
                      onChange={(e) => patchDudaAccount(index, { apiUser: e.target.value })}
                    />
                    <SecretField
                      label="API password"
                      fullWidth
                      size="small"
                      secretPath={dudaPasswordSecretPath(acc.id)}
                      {...secretProps}
                      value={acc.apiPassword}
                      onChange={(e) => patchDudaAccount(index, { apiPassword: e.target.value })}
                    />
                    <TextField
                      label="Default site"
                      fullWidth
                      size="small"
                      value={acc.siteName}
                      onChange={(e) => patchDudaAccount(index, { siteName: e.target.value })}
                    />
                    <Button
                      variant="outlined"
                      startIcon={
                        testing === `duda-${acc.id}` ? <CircularProgress size={18} /> : <ScienceOutlinedIcon />
                      }
                      disabled={testing !== null}
                      onClick={() => void runTest(`duda-${acc.id}`)}
                    >
                      Test this account
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={() => void saveSection('duda')}>
              Save all Duda accounts
            </Button>
          </Stack>
        );

      case 'meta':
        return (
          <Stack spacing={3} maxWidth={720}>
            <Typography variant="body2" color="text.secondary">
              Connect Facebook Pages and Instagram Business accounts. Add as many as you need — tokens are stored per
              account in Secret Manager.
            </Typography>
            <TextField
              label="Meta App ID"
              fullWidth
              size="small"
              value={draft.meta.appId}
              onChange={(e) => setDraft((d) => ({ ...d, meta: { ...d.meta, appId: e.target.value } }))}
            />
            <SecretField
              label="Meta App Secret"
              fullWidth
              size="small"
              secretPath={SECRET_FIELD_PATHS.metaAppSecret}
              {...secretProps}
              value={draft.meta.appSecret}
              onChange={(e) => setDraft((d) => ({ ...d, meta: { ...d.meta, appSecret: e.target.value } }))}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl fullWidth size="small" sx={{ maxWidth: 400 }}>
                <InputLabel>Default active account</InputLabel>
                <Select
                  label="Default active account"
                  value={draft.meta.activeAccountId}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, meta: { ...d.meta, activeAccountId: String(e.target.value) } }))
                  }
                >
                  {draft.meta.accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button startIcon={<AddIcon />} onClick={addMetaAccount}>
                Add account
              </Button>
            </Stack>
            {draft.meta.accounts.map((acc, index) => (
              <Card key={acc.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {acc.label}
                    </Typography>
                    {draft.meta.accounts.length > 1 && (
                      <IconButton size="small" color="error" onClick={() => removeMetaAccount(index)} aria-label="Remove">
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack spacing={2}>
                    <TextField
                      label="Label"
                      fullWidth
                      size="small"
                      value={acc.label}
                      onChange={(e) => patchMetaAccount(index, { label: e.target.value })}
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>Platform</InputLabel>
                      <Select
                        label="Platform"
                        value={acc.platform}
                        onChange={(e) =>
                          patchMetaAccount(index, { platform: e.target.value as MetaAccount['platform'] })
                        }
                      >
                        <MenuItem value="both">Facebook + Instagram</MenuItem>
                        <MenuItem value="facebook">Facebook only</MenuItem>
                        <MenuItem value="instagram">Instagram only</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Facebook Page ID"
                      fullWidth
                      size="small"
                      value={acc.pageId}
                      onChange={(e) => patchMetaAccount(index, { pageId: e.target.value })}
                    />
                    <TextField
                      label="Instagram Business ID"
                      fullWidth
                      size="small"
                      value={acc.instagramBusinessId}
                      onChange={(e) => patchMetaAccount(index, { instagramBusinessId: e.target.value })}
                    />
                    <SecretField
                      label="Page access token"
                      fullWidth
                      size="small"
                      secretPath={metaAccessTokenSecretPath(acc.id)}
                      {...secretProps}
                      value={acc.accessToken}
                      onChange={(e) => patchMetaAccount(index, { accessToken: e.target.value })}
                    />
                    <Button
                      variant="outlined"
                      startIcon={
                        testing === `meta-${acc.id}` ? <CircularProgress size={18} /> : <ScienceOutlinedIcon />
                      }
                      disabled={testing !== null}
                      onClick={() => void runTest(`meta-${acc.id}`)}
                    >
                      Test connection
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={() => void saveSection('meta')}>
              Save all Meta accounts
            </Button>
          </Stack>
        );

      case 'search-console':
        return (
          <Stack spacing={2} maxWidth={640}>
            <TextField
              label="Site URL"
              fullWidth
              value={draft.searchConsole.siteUrl}
              onChange={(e) =>
                setDraft((d) => ({ ...d, searchConsole: { ...d.searchConsole, siteUrl: e.target.value } }))
              }
              helperText="sc-domain:example.com or https://www.example.com/"
            />
            <SecretField
              label="Service account JSON"
              fullWidth
              multiline
              minRows={4}
              secretPath={SECRET_FIELD_PATHS.searchConsoleSa}
              {...secretProps}
              value={draft.searchConsole.serviceAccountJson}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  searchConsole: { ...d.searchConsole, serviceAccountJson: e.target.value },
                }))
              }
            />
            <SaveTestRow testKey="searchConsole" saveKey="searchConsole" />
          </Stack>
        );

      case 'google-ads':
        return (
          <Stack spacing={2} maxWidth={560}>
            <TextField
              label="Developer token"
              fullWidth
              value={draft.googleAds.developerToken}
              onChange={(e) =>
                setDraft((d) => ({ ...d, googleAds: { ...d.googleAds, developerToken: e.target.value } }))
              }
            />
            <TextField
              label="OAuth client ID"
              fullWidth
              value={draft.googleAds.clientId}
              onChange={(e) => setDraft((d) => ({ ...d, googleAds: { ...d.googleAds, clientId: e.target.value } }))}
            />
            <SecretField
              label="OAuth client secret"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.googleAdsClientSecret}
              {...secretProps}
              value={draft.googleAds.clientSecret}
              onChange={(e) =>
                setDraft((d) => ({ ...d, googleAds: { ...d.googleAds, clientSecret: e.target.value } }))
              }
            />
            <TextField
              label="Customer ID"
              fullWidth
              value={draft.googleAds.customerId}
              onChange={(e) =>
                setDraft((d) => ({ ...d, googleAds: { ...d.googleAds, customerId: e.target.value } }))
              }
            />
            <TextField
              label="Login customer ID (MCC, optional)"
              fullWidth
              value={draft.googleAds.loginCustomerId}
              onChange={(e) =>
                setDraft((d) => ({ ...d, googleAds: { ...d.googleAds, loginCustomerId: e.target.value } }))
              }
            />
            <SecretField
              label="Refresh token"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.googleAdsRefreshToken}
              {...secretProps}
              value={draft.googleAds.refreshToken}
              onChange={(e) =>
                setDraft((d) => ({ ...d, googleAds: { ...d.googleAds, refreshToken: e.target.value } }))
              }
            />
            <SaveTestRow testKey="googleAds" saveKey="googleAds" />
          </Stack>
        );

      case 'ga4':
        return (
          <Stack spacing={2} maxWidth={640}>
            <TextField
              label="Property ID"
              fullWidth
              value={draft.ga4.propertyId}
              onChange={(e) => setDraft((d) => ({ ...d, ga4: { ...d.ga4, propertyId: e.target.value } }))}
              helperText="Numeric GA4 property ID"
            />
            <TextField
              label="Measurement ID (optional)"
              fullWidth
              value={draft.ga4.measurementId}
              onChange={(e) => setDraft((d) => ({ ...d, ga4: { ...d.ga4, measurementId: e.target.value } }))}
              helperText="G-XXXXXXXX for client-side tagging reference"
            />
            <SecretField
              label="Service account JSON"
              fullWidth
              multiline
              minRows={4}
              secretPath={SECRET_FIELD_PATHS.ga4Sa}
              {...secretProps}
              value={draft.ga4.serviceAccountJson}
              onChange={(e) =>
                setDraft((d) => ({ ...d, ga4: { ...d.ga4, serviceAccountJson: e.target.value } }))
              }
            />
            <SaveTestRow testKey="ga4" saveKey="ga4" />
          </Stack>
        );

      case 'merchant-center':
        return (
          <Stack spacing={2} maxWidth={640}>
            <TextField
              label="Merchant Center ID"
              fullWidth
              value={draft.merchantCenter.merchantId}
              onChange={(e) =>
                setDraft((d) => ({ ...d, merchantCenter: { ...d.merchantCenter, merchantId: e.target.value } }))
              }
            />
            <SecretField
              label="Service account JSON"
              fullWidth
              multiline
              minRows={4}
              secretPath={SECRET_FIELD_PATHS.merchantCenterSa}
              {...secretProps}
              value={draft.merchantCenter.serviceAccountJson}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  merchantCenter: { ...d.merchantCenter, serviceAccountJson: e.target.value },
                }))
              }
            />
            <SaveTestRow testKey="merchantCenter" saveKey="merchantCenter" />
          </Stack>
        );

      case 'slack':
        return (
          <Stack spacing={2} maxWidth={560}>
            <TextField
              label="Workspace name"
              fullWidth
              value={draft.slack.workspaceName}
              onChange={(e) => setDraft((d) => ({ ...d, slack: { ...d.slack, workspaceName: e.target.value } }))}
            />
            <TextField
              label="Default channel ID"
              fullWidth
              value={draft.slack.channelId}
              onChange={(e) => setDraft((d) => ({ ...d, slack: { ...d.slack, channelId: e.target.value } }))}
            />
            <SecretField
              label="Bot token"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.slackBotToken}
              {...secretProps}
              value={draft.slack.botToken}
              onChange={(e) => setDraft((d) => ({ ...d, slack: { ...d.slack, botToken: e.target.value } }))}
            />
            <SecretField
              label="Signing secret"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.slackSigningSecret}
              {...secretProps}
              value={draft.slack.signingSecret}
              onChange={(e) => setDraft((d) => ({ ...d, slack: { ...d.slack, signingSecret: e.target.value } }))}
            />
            <SaveTestRow testKey="slack" saveKey="slack" />
          </Stack>
        );

      case 'twilio':
        return (
          <Stack spacing={2} maxWidth={560}>
            <TextField
              label="Account SID"
              fullWidth
              value={draft.twilio.accountSid}
              onChange={(e) => setDraft((d) => ({ ...d, twilio: { ...d.twilio, accountSid: e.target.value } }))}
            />
            <SecretField
              label="Auth token"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.twilioAuthToken}
              {...secretProps}
              value={draft.twilio.authToken}
              onChange={(e) => setDraft((d) => ({ ...d, twilio: { ...d.twilio, authToken: e.target.value } }))}
            />
            <TextField
              label="Phone number"
              fullWidth
              value={draft.twilio.phoneNumber}
              onChange={(e) => setDraft((d) => ({ ...d, twilio: { ...d.twilio, phoneNumber: e.target.value } }))}
            />
            <TextField
              label="Messaging service SID (optional)"
              fullWidth
              value={draft.twilio.messagingServiceSid}
              onChange={(e) =>
                setDraft((d) => ({ ...d, twilio: { ...d.twilio, messagingServiceSid: e.target.value } }))
              }
            />
            <SaveTestRow testKey="twilio" saveKey="twilio" />
          </Stack>
        );

      case 'wizzard':
        return (
          <Stack spacing={2} maxWidth={720}>
            <Alert severity="info">
              Wizzard uses the{' '}
              <Link href="https://console.x.ai/" target="_blank" rel="noopener noreferrer">
                xAI Grok API
              </Link>{' '}
              to draft payment reminders and answer questions about your Stripe + Xero data. Keys stay on the server.
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.wizzard.enabled}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, wizzard: { ...d.wizzard, enabled: e.target.checked } }))
                  }
                />
              }
              label="Enable Wizzard copilot"
            />
            <SecretField
              label="Grok API key"
              fullWidth
              secretPath={SECRET_FIELD_PATHS.wizzardApiKey}
              {...secretProps}
              value={draft.wizzard.apiKey}
              onChange={(e) => setDraft((d) => ({ ...d, wizzard: { ...d.wizzard, apiKey: e.target.value } }))}
            />
            <TextField
              select
              label="Model"
              fullWidth
              value={draft.wizzard.model || 'grok-3-mini'}
              onChange={(e) => setDraft((d) => ({ ...d, wizzard: { ...d.wizzard, model: e.target.value } }))}
            >
              <MenuItem value="grok-3-mini">grok-3-mini (fast, lower cost)</MenuItem>
              <MenuItem value="grok-3">grok-3</MenuItem>
              <MenuItem value="grok-2-latest">grok-2-latest</MenuItem>
            </TextField>
            <SaveTestRow testKey="wizzard" saveKey="wizzard" />
          </Stack>
        );

      default:
        return null;
    }
  };

  const activeLabel = SECTIONS.find((s) => s.id === section)?.label ?? 'Integrations';

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        API Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
        Connect email, payments, accounting, websites, Google marketing tools, Slack, and Twilio. Sensitive values
        are stored in{' '}
        {backend ? (
          <>
            <strong>Google Secret Manager</strong> ({secretsStorage === 'local' ? 'local fallback — set GCP_PROJECT' : 'enabled'})
          </>
        ) : (
          'this browser until you sign in with the Cloud Run backend'
        )}
        .
      </Typography>

      {backend && (
        <Alert severity="info" icon={<LockOutlinedIcon />} sx={{ mb: 2, maxWidth: 900 }}>
          Secret fields show a <strong>Saved</strong> badge when a value is already in Secret Manager. Enter a new value
          only when rotating credentials.
        </Alert>
      )}

      {message && (
        <Alert severity={message.type} sx={{ mb: 2, maxWidth: 900 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3, maxWidth: 900 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Workspace
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label="Workspace display name"
              fullWidth
              value={workspaceDraft}
              onChange={(e) => setWorkspaceDraft(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={() => updateSettings((s) => ({ ...s, workspace: { name: workspaceDraft } }))}
              sx={{ minWidth: 120 }}
            >
              Save
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
        <Card sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
          <List dense disablePadding>
            {SECTIONS.map((s) => (
              <ListItemButton
                key={s.id}
                selected={section === s.id}
                onClick={() => selectSection(s.id)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{s.icon}</ListItemIcon>
                <ListItemText primary={s.label} primaryTypographyProps={{ variant: 'body2', fontWeight: section === s.id ? 700 : 400 }} />
              </ListItemButton>
            ))}
          </List>
        </Card>

        <Card sx={{ flex: 1, minWidth: 0, maxWidth: 900 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Typography variant="h6">{activeLabel}</Typography>
              {backend && configuredSecrets.length > 0 && (
                <Chip size="small" label={`${configuredSecrets.length} secrets saved`} color="success" variant="outlined" />
              )}
            </Stack>
            <Divider sx={{ mb: 3 }} />
            {renderPanel()}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default ApiSettings;
